// src/components/SideFilters/components/SideFilterMultiSelect/SideFilterMultiSelect.tsx
import React, { useMemo, useState } from 'react';
import './sideFilterMultiSelect.less';
import { Button, Checkbox, Input, Skeleton, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import {
  updateModalSideFilters,
  updateSiteFilter,
} from '../../../../modules/SearchDatePicker/searchDatePickerSlice';
import { MultiSelectFilter, SideFilterAttr } from '../../interface';
import useGetAttributeTranslation, {
  replaceFlightNumberPrefix,
} from '../../hooks/useGetAttributeTranslation';
import { toNumber, uniqBy } from 'lodash';
import {
  addToCurrencyString,
  roundUpCurrency,
} from '@skygroup/shared/utils/roundUpCurrency';
import { AiOutlineQuestionCircle } from 'react-icons/ai';

interface Props {
  filter: MultiSelectFilter;
  isHasSearch?: boolean;
  max_items_to_show?: number;
  isNights?: boolean;
  withModalScopeParams?: boolean;
  isLoading?: boolean;
  additionalPrice?: number;
  freeCancellationTooltipText?: string;
  localSideFilters?: Record<string, any> | null;
  setLocalSideFilters?: (f: Record<string, any>) => void;
}

const SideFilterMultiSelect = ({
  filter,
  isHasSearch = true,
  max_items_to_show = 6,
  isNights = false,
  withModalScopeParams,
  isLoading,
  additionalPrice = 0,
  freeCancellationTooltipText,
  localSideFilters,
  setLocalSideFilters,
}: Props) => {
  const dispatch = useDispatch();
  const { sideFilters: reduxSideFilters, modalSideFilters } = useSelector(
    (state: RootState) => {
      return state.search;
    }
  );

  // localSideFilters takes precedence when provided
  const sideFilters = useMemo(
    () =>
      localSideFilters
        ? localSideFilters
        : withModalScopeParams
        ? modalSideFilters
        : reduxSideFilters,
    [withModalScopeParams, modalSideFilters, reduxSideFilters, localSideFilters]
  );

  const [searchKeyWord, setSearchKeyWord] = useState('');
  const [showAll, setShowAll] = useState(false);

  const isOneLine = filter.attr === 'hotel';
  if (filter.attr === 'hotel') {
    filter.options = filter.options.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  }

  const {
    general: t,
    sideFilters: side_filters_t,
    boards: baseBoardT,
    hotelTypes,
    package_feature: t_package_feature,
  } = useSelector((state: RootState) => state.config.translations);

  // Helper: commit changes either to local state or to redux (modal/global)
  const commitFilter = (key: string, value: any) => {
    if (typeof setLocalSideFilters === 'function') {
      setLocalSideFilters({
        ...(localSideFilters || {}),
        [key]: value,
      });
      return;
    }
    const fn = withModalScopeParams ? updateModalSideFilters : updateSiteFilter;
    dispatch(fn({ key, value }));
  };

  // handleChange uses same semantics as before but commits via commitFilter
  const handleChange = (newValue: any[]) => {
    let value = [...newValue];

    // For freeCancellation we only want newly checked values (preserve old behaviour)
    if (filter.attr === 'freeCancellation') {
      const existing = sideFilters?.[filter.attr] ?? [];
      value = newValue.filter((el) => !existing?.includes(el));
    }

    // commit change
    commitFilter(filter.attr, value);
  };

  const options = useMemo(() => {
    const allRawOptions = uniqBy(
      filter.options,
      (obj) => obj.id || obj.name || obj.range
    );

    const searchTerm = searchKeyWord.trim().toLowerCase();
    const getFilteredOptions = () => {
      // No search term → show all options
      if (searchTerm.length === 0) {
        return allRawOptions;
      }

      // Filter options by search term
      const matched = allRawOptions.filter((option) => {
        // Keep options without names visible
        if (!option.name) return true;

        return option.name.toLowerCase().includes(searchTerm);
      });

      // If no matches, show full list rather than empty state
      return matched.length > 0 ? matched : allRawOptions;
    };

    const rawOptions = getFilteredOptions();

    const results = rawOptions
      .map((option) => {
        return {
          ...option,
          value: option.id ?? option.name ?? option.range,
          label: (
            <div>
              {(() => {
                switch (replaceFlightNumberPrefix(filter.attr)) {
                  case SideFilterAttr.Feature:
                    return (
                      <div className="label-name">
                        {t_package_feature?.[option?.id] ?? option.id}
                      </div>
                    );
                  case SideFilterAttr.HotelType:
                    return (
                      <div className="label-name">
                        {hotelTypes[option.id] ?? option.id}
                      </div>
                    );
                  case SideFilterAttr.FreeCancellation:
                    return (
                      <div className="label-name">
                        {side_filters_t.freeCancellationLabels[option.id] ??
                          option.id}
                      </div>
                    );
                  case SideFilterAttr.TsType:
                    return (
                      <div className="label-name">
                        {side_filters_t[option.id]}
                      </div>
                    );
                  case SideFilterAttr.BoardBasis:
                    return (
                      <div className="label-name">
                        {!baseBoardT[option.id]
                          ? option.id
                          : baseBoardT[option.id]}
                      </div>
                    );
                  case SideFilterAttr.Stops:
                    const stopsNum = Number(option.id);
                    return (
                      <div className="label-name">
                        {stopsNum === 0
                          ? side_filters_t.nonStop
                          : stopsNum === 1
                          ? side_filters_t.stop
                          : stopsNum + ' ' + side_filters_t.stops_plural}
                      </div>
                    );

                  default:
                    return (
                      <div className="label-name">
                        {option.range ??
                          option.name ??
                          t[option.id] ??
                          option.id}{' '}
                        {isNights && t.nights}
                      </div>
                    );
                }
              })()}

              {option.min && (
                <div className="price">
                  {t.minPrice}{' '}
                  <b>
                    {roundUpCurrency(
                      addToCurrencyString(option.min, additionalPrice)
                    )}
                  </b>
                </div>
              )}
            </div>
          ),
        };
      })
      // Add this filter to exclude invalid options
      .filter((option) => option.value !== undefined && option.value !== null);

    if (showAll) {
      return results;
    }
    return results.slice(0, max_items_to_show);
  }, [
    max_items_to_show,
    searchKeyWord,
    showAll,
    filter.options,
    filter.attr,
    additionalPrice,
    isNights,
    t,
    t_package_feature,
    hotelTypes,
    baseBoardT,
    side_filters_t,
  ]);
  const attributeTranslation = useGetAttributeTranslation();

  if (!filter || !filter?.options?.length) {
    return null;
  }

  if (isLoading) {
    return <Skeleton title={false} active paragraph={{ rows: 1 }} />;
  }

  return (
    <div className={`side-filter-multi-select ${isOneLine ? 'one-line' : ''}`}>
      <Tooltip
        title={
          freeCancellationTooltipText && filter.attr === 'freeCancellation'
            ? freeCancellationTooltipText
            : undefined
        }
        className={`side-filter-multi-select-tooltip ${
          freeCancellationTooltipText && filter.attr === 'freeCancellation'
            ? 'active'
            : ''
        }`}
      >
        <h2>{attributeTranslation(filter.attr)}</h2>
        {freeCancellationTooltipText && filter.attr === 'freeCancellation' && (
          <AiOutlineQuestionCircle size={18} className={'hint'} />
        )}
      </Tooltip>

      {isHasSearch && filter.options[0]?.name && (
        <Input
          onChange={(e) => {
            setSearchKeyWord(e.target.value);
          }}
          value={searchKeyWord}
          className="input"
          placeholder={t.search}
          allowClear
        />
      )}
      <Checkbox.Group
        className="check-box-group"
        options={options.map((option) => {
          // SOMETIMES VALUE IS NUMBER SOMETIMES IT"S STRING IF POSSIBLE TO CONVERT CONVERT TO NUMBER
          if (isFinite(toNumber(option.value))) {
            return {
              ...option,
              value: toNumber(option.value),
            };
          }
          return option;
        })}
        onChange={(e: any) => handleChange(e)}
        value={(sideFilters?.[filter.attr] ?? []).filter(
          (v: any) => v !== null && v !== undefined
        )}
      />
      {options.length >= max_items_to_show && (
        <Button
          onClick={() => {
            setShowAll((prev) => !prev);
          }}
        >
          {showAll ? t.showLess : t.showAll}
        </Button>
      )}
    </div>
  );
};

export default SideFilterMultiSelect;
